// In-memory Users & roles for demo mode (no Supabase configured).
import type { InvitationRow, InviteResult, MemberRow, RoleRow } from "./api";

const roles: RoleRow[] = [
  { id: "role-admin", code: "admin", name: "Administrator", is_system: true },
  { id: "role-viewer", code: "viewer", name: "Viewer", is_system: true },
  { id: "role-warehouse", code: "warehouse", name: "Warehouse operator", is_system: false },
];

const day = 86_400_000;
let members: MemberRow[] = [
  {
    user_id: "demo",
    email: "demo@patronix.local",
    full_name: "Demo User",
    status: "active",
    role_ids: ["role-admin"],
    joined_at: new Date(Date.now() - 40 * day).toISOString(),
    last_sign_in_at: new Date().toISOString(),
    is_self: true,
  },
  {
    user_id: "demo-anna",
    email: "anna.kowalska@example.com",
    full_name: "Anna Kowalska",
    status: "active",
    role_ids: ["role-warehouse"],
    joined_at: new Date(Date.now() - 20 * day).toISOString(),
    last_sign_in_at: new Date(Date.now() - day).toISOString(),
    is_self: false,
  },
];

let invitations: InvitationRow[] = [
  {
    id: "inv-1",
    email: "piotr.nowak@example.com",
    full_name: "Piotr Nowak",
    role_id: "role-viewer",
    status: "pending",
    expires_at: new Date(Date.now() + 10 * day).toISOString(),
    created_at: new Date(Date.now() - 4 * day).toISOString(),
    accepted_at: null,
  },
];

const delay = <T,>(value: T) => new Promise<T>((resolve) => setTimeout(() => resolve(value), 120));

export const demoUsersApi = {
  members: () => delay(members.map((m) => ({ ...m }))),
  invitations: () => delay(invitations.map((i) => ({ ...i }))),
  roles: () => delay(roles),
  invite(email: string, roleId: string, fullName: string | null): Promise<InviteResult> {
    const normalized = email.trim().toLowerCase();
    if (members.some((m) => m.email === normalized)) {
      return Promise.reject(new Error("This person is already a member of the organization"));
    }
    const expires = new Date(Date.now() + 14 * day).toISOString();
    const existing = invitations.find((i) => i.email === normalized && i.status === "pending");
    if (existing) {
      existing.role_id = roleId;
      existing.full_name = fullName ?? existing.full_name;
      existing.expires_at = expires;
      return delay({ invitation_id: existing.id, token: crypto.randomUUID().replaceAll("-", ""), expires_at: expires });
    }
    const id = `inv-${Date.now()}`;
    invitations = [
      { id, email: normalized, full_name: fullName, role_id: roleId, status: "pending", expires_at: expires, created_at: new Date().toISOString(), accepted_at: null },
      ...invitations,
    ];
    return delay({ invitation_id: id, token: crypto.randomUUID().replaceAll("-", ""), expires_at: expires });
  },
  revoke(id: string): Promise<void> {
    invitations = invitations.map((i) => (i.id === id && i.status === "pending" ? { ...i, status: "revoked" } : i));
    return delay(undefined);
  },
  setRole(userId: string, roleId: string, granted: boolean): Promise<void> {
    members = members.map((m) =>
      m.user_id !== userId
        ? m
        : { ...m, role_ids: granted ? [...new Set([...m.role_ids, roleId])] : m.role_ids.filter((r) => r !== roleId) },
    );
    return delay(undefined);
  },
  setStatus(userId: string, status: "active" | "disabled"): Promise<void> {
    members = members.map((m) => (m.user_id === userId ? { ...m, status } : m));
    return delay(undefined);
  },
};
